import { useState, useEffect } from 'react';
import { IconCookie, IconX, IconInfoCircle } from '@tabler/icons-react';
import { Modal } from 'antd';
import clsx from 'clsx';
import { useTheme } from '../../contexts/ThemeContext';
import './CookieBanner.scss';

const COOKIE_CONSENT_KEY = 'cookie-consent';

type CookieConsent = 'accepted' | 'rejected' | null;

const CookieBanner = () => {
    const [showBanner, setShowBanner] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const { theme } = useTheme();

    useEffect(() => {
        // Check if user has already made a choice
        const consent = localStorage.getItem(COOKIE_CONSENT_KEY) as CookieConsent;
        if (!consent) {
            // Show banner after a short delay for better UX
            const timer = setTimeout(() => {
                setShowBanner(true);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleAccept = () => {
        localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
        setShowBanner(false);
        // Here you can add logic to enable analytics, tracking, etc.
    };

    const handleReject = () => {
        localStorage.setItem(COOKIE_CONSENT_KEY, 'rejected');
        setShowBanner(false);
        // Here you can add logic to disable analytics, tracking, etc.
    };

    if (!showBanner) return null;

    return (
        <div
            className={clsx(
                'fixed bottom-0 left-0 right-0 z-[var(--z-cookie-banner)]',
                'transition-all duration-500 ease-in-out',
                showBanner ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0',
                'shadow-2xl'
            )}
        >
            <div
                className={clsx(
                    'mx-2 md:mx-4 mb-4 md:mb-6 max-w-4xl md:mx-auto',
                    'rounded-xl border-2 p-4 md:p-6',
                    'bg-white border-gray-200',
                    'dark:bg-surface-dark dark:border-borders',
                    'backdrop-blur-sm'
                )}
            >
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                    {/* Icon and content */}
                    <div className="flex items-start gap-3 flex-1">
                        <div
                            className={clsx(
                                'flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center',
                                'bg-gradient-to-br from-amber-500 to-orange-500',
                                'dark:from-amber-600 dark:to-orange-600',
                                'shadow-md'
                            )}
                        >
                            <IconCookie size={20} className="text-white" strokeWidth={2} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3
                                className={clsx(
                                    'font-bold text-sm md:text-base mb-1 transition-colors duration-300',
                                    'text-gray-900 dark:text-white'
                                )}
                            >
                                Uso de Cookies
                            </h3>
                            <p
                                className={clsx(
                                    'text-xs md:text-sm leading-relaxed transition-colors duration-300',
                                    'text-gray-600 dark:text-surface-light'
                                )}
                            >
                                Utilizamos cookies para mejorar tu experiencia, analizar el tráfico del sitio y personalizar el contenido.
                                Al continuar navegando, aceptas nuestro uso de cookies.{' '}
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowModal(true);
                                    }}
                                    className={clsx(
                                        'underline font-medium transition-colors duration-300',
                                        'text-blue-600 hover:text-blue-700',
                                        'dark:text-blue-400 dark:hover:text-blue-300',
                                        'bg-transparent border-none p-0 cursor-pointer'
                                    )}
                                >
                                    Más información
                                </button>
                            </p>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 w-full md:w-auto flex-shrink-0">
                        <button
                            onClick={handleReject}
                            className={clsx(
                                'px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300',
                                'border-2 border-gray-300 dark:border-gray-600',
                                'text-gray-700 dark:text-gray-300',
                                'bg-transparent hover:bg-gray-100 dark:hover:bg-surface-medium',
                                'hover:border-gray-400 dark:hover:border-gray-500',
                                'focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2',
                                'dark:focus:ring-offset-surface-dark'
                            )}
                        >
                            Rechazar
                        </button>
                        <button
                            onClick={handleAccept}
                            className={clsx(
                                'px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300',
                                'bg-gradient-to-r from-blue-600 to-indigo-600 text-white',
                                'dark:from-blue-500 dark:to-indigo-500',
                                'hover:from-blue-700 hover:to-indigo-700',
                                'dark:hover:from-blue-600 dark:hover:to-indigo-600',
                                'shadow-md hover:shadow-lg',
                                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                                'dark:focus:ring-offset-surface-dark'
                            )}
                        >
                            Aceptar
                        </button>
                        <button
                            onClick={handleReject}
                            className={clsx(
                                'p-2 rounded-lg transition-all duration-300',
                                'text-gray-500 dark:text-gray-400',
                                'hover:bg-gray-100 dark:hover:bg-surface-medium',
                                'focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2',
                                'dark:focus:ring-offset-surface-dark',
                                'md:hidden'
                            )}
                            aria-label="Cerrar"
                        >
                            <IconX size={18} strokeWidth={2} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Cookie Information Modal */}
            <Modal
                open={showModal}
                onCancel={() => setShowModal(false)}
                footer={null}
                width={600}
                className={clsx(
                    'cookie-info-modal',
                    theme === 'dark' ? 'dark-modal' : 'light-modal'
                )}
                wrapClassName={clsx(
                    'cookie-info-modal-wrapper',
                    theme === 'dark' ? 'dark-modal-wrapper' : 'light-modal-wrapper',
                    'bottom-sheet-mobile'
                )}
                title={
                    <div className={clsx(
                        'flex items-center gap-3',
                        'text-gray-900 dark:text-white'
                    )}>
                        <div className={clsx(
                            'w-10 h-10 rounded-lg flex items-center justify-center',
                            'bg-gradient-to-br from-amber-500 to-orange-500',
                            'dark:from-amber-600 dark:to-orange-600'
                        )}>
                            <IconCookie size={20} className="text-white" strokeWidth={2} />
                        </div>
                        <span className="text-xl font-bold">Política de Cookies</span>
                    </div>
                }
                centered={false}
                maskStyle={{
                    backdropFilter: 'blur(4px)',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)'
                }}
            >
                <div className={clsx(
                    'space-y-4',
                    'text-gray-700 dark:text-gray-300'
                )}>
                    <div>
                        <h3 className={clsx(
                            'font-semibold text-base mb-2',
                            'text-gray-900 dark:text-white'
                        )}>
                            ¿Qué son las cookies?
                        </h3>
                        <p className="text-sm leading-relaxed">
                            Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo cuando visitas un sitio web.
                            Nos ayudan a recordar tus preferencias y mejorar tu experiencia de navegación.
                        </p>
                    </div>

                    <div>
                        <h3 className={clsx(
                            'font-semibold text-base mb-2',
                            'text-gray-900 dark:text-white'
                        )}>
                            ¿Qué tipos de cookies utilizamos?
                        </h3>
                        <div className="space-y-3">
                            <div className={clsx(
                                'p-3 rounded-lg border',
                                'bg-gray-50 dark:bg-surface-medium',
                                'border-gray-200 dark:border-borders'
                            )}>
                                <h4 className={clsx(
                                    'font-medium text-sm mb-1',
                                    'text-gray-900 dark:text-white'
                                )}>
                                    Cookies esenciales
                                </h4>
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                    Necesarias para el funcionamiento básico del sitio. Incluyen autenticación, seguridad y preferencias de sesión.
                                </p>
                            </div>
                            <div className={clsx(
                                'p-3 rounded-lg border',
                                'bg-gray-50 dark:bg-surface-medium',
                                'border-gray-200 dark:border-borders'
                            )}>
                                <h4 className={clsx(
                                    'font-medium text-sm mb-1',
                                    'text-gray-900 dark:text-white'
                                )}>
                                    Cookies de rendimiento
                                </h4>
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                    Nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web, recopilando información de forma anónima.
                                </p>
                            </div>
                            <div className={clsx(
                                'p-3 rounded-lg border',
                                'bg-gray-50 dark:bg-surface-medium',
                                'border-gray-200 dark:border-borders'
                            )}>
                                <h4 className={clsx(
                                    'font-medium text-sm mb-1',
                                    'text-gray-900 dark:text-white'
                                )}>
                                    Cookies de funcionalidad
                                </h4>
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                    Permiten que el sitio web recuerde tus preferencias (como idioma o región) para ofrecerte una experiencia más personalizada.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className={clsx(
                            'font-semibold text-base mb-2',
                            'text-gray-900 dark:text-white'
                        )}>
                            ¿Cómo gestionar las cookies?
                        </h3>
                        <p className="text-sm leading-relaxed">
                            Puedes aceptar o rechazar las cookies no esenciales haciendo clic en los botones del banner.
                            También puedes gestionar las cookies desde la configuración de tu navegador.
                            Ten en cuenta que rechazar algunas cookies puede afectar la funcionalidad del sitio.
                        </p>
                    </div>

                    <div className={clsx(
                        'p-3 rounded-lg',
                        'bg-blue-50 dark:bg-blue-900/20',
                        'border border-blue-200 dark:border-blue-800/50'
                    )}>
                        <div className="flex items-start gap-2">
                            <IconInfoCircle size={18} className={clsx(
                                'text-blue-600 dark:text-blue-400',
                                'flex-shrink-0 mt-0.5'
                            )} />
                            <p className={clsx(
                                'text-xs leading-relaxed',
                                'text-blue-800 dark:text-blue-200'
                            )}>
                                <strong>Nota:</strong> Las cookies esenciales no se pueden desactivar ya que son necesarias para el funcionamiento del sitio.
                            </p>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            onClick={() => setShowModal(false)}
                            className={clsx(
                                'px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300',
                                'bg-gradient-to-r from-blue-600 to-indigo-600 text-white',
                                'dark:from-blue-500 dark:to-indigo-500',
                                'hover:from-blue-700 hover:to-indigo-700',
                                'dark:hover:from-blue-600 dark:hover:to-indigo-600',
                                'shadow-md hover:shadow-lg'
                            )}
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export { CookieBanner };
